import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TitleService } from 'core/services';

@Injectable()
export class AppTitleStrategyService extends TitleStrategy implements OnDestroy {
  private pageTitleSubscription: Subscription = new Subscription();

  /**
   * @param title - Angular's {@link Title} service used to set the browser tab title.
   * @param translate - `ngx-translate` service (injected for potential future i18n use).
   * @param pageTitleService - Application-level service that owns the canonical title state.
   */
  constructor(private title: Title, private translate: TranslateService, private pageTitleService: TitleService) {
    super();
    this.pageTitleSubscription = this.pageTitleService.getTitle$().subscribe((t) => {
      this.title.setTitle(t);
    });
  }

  /**
   * Called by the Angular router after every successful navigation.
   *
   * Collects all route titles from the snapshot tree, delegates formatting to
   * {@link TitleService.setSectionTitles}, and applies the result to the browser
   * tab via {@link Title.setTitle}. Exits early when no titles are found.
   *
   * @param snapshot - The current router state snapshot after navigation completes.
   */
  public override updateTitle(snapshot: RouterStateSnapshot): void {
    const titles = this.collectTitles(snapshot.root);
    if (!titles.length) return;

    const parts = this.pageTitleService.setSectionTitles(titles);
    this.title.setTitle(parts);
  }

  /**
   * Cleans up the reactive title subscription to prevent memory leaks when the
   * service is destroyed.
   */
  public ngOnDestroy(): void {
    this.pageTitleSubscription.unsubscribe();
  }

  /**
   * Recursively walks the activated route snapshot tree and collects resolved
   * titles for every node that declares one.
   *
   * @param route - The root (or current) {@link ActivatedRouteSnapshot} to traverse.
   * @returns A flat array of resolved title strings (or snapshot objects) gathered
   *   from the route and all its descendants.
   */
  private collectTitles(route: ActivatedRouteSnapshot): string[] {
    const result: any[] = [];

    if (route.title) result.push(this.resolveRouteTitle(route));
    route.children.forEach((child) => result.push(...this.collectTitles(child)));
    return result;
  }

  /**
   * Resolves a route's title, substituting parameter placeholders with actual values.
   *
   * When a route's `title` starts with `:`, it is treated as a reference to a route
   * parameter. The method looks up the corresponding value in `route.paramMap` and
   * then searches `route.data` for an object whose `id` matches that value and that
   * exposes a `name` property. The best available value (`name`, param value, or the
   * original placeholder) is returned as the resolved title.
   *
   * @param route - The {@link ActivatedRouteSnapshot} whose title should be resolved.
   * @returns The original snapshot when the title is a plain string, or a new object
   *   with the `title` property replaced by the resolved human-readable name.
   */
  private resolveRouteTitle(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot | { title: string } {
    const title = route.title as string;
    if (!title?.startsWith(':')) return route;

    const paramName = title.slice(1);
    const paramValue = route.paramMap.get(paramName);
    const resolved = Object.values(route.data).find((value) => value?.id === paramValue && value?.name);
    return { ...route, title: resolved?.name ?? paramValue ?? title };
  }
}
